import React from "react";
import Markdown from "markdown-to-jsx";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];


const MarkdownWrapper = ({ children }) => {
	const history = useHistory();
	const { user } = React.useContext(AuthContext);

	const openNumber = React.useCallback(async (rawDigits) => {
		let digits = String(rawDigits || "").replace(/\D/g, "");
		if (!digits) return;
		if (digits.length <= 11) digits = "55" + digits;
		try {
			const { data } = await api.get("contacts", { params: { searchParam: digits } });
			let contact = (data.contacts || []).find(
				c => String(c.number || "").replace(/\D/g, "") === digits
			);
			if (!contact) {
				const { data: created } = await api.post("/contacts", { name: digits, number: digits });
				contact = created;
			}
			const queues = user?.queues || [];
			const atend = queues.find(q => String(q.name || "").toLowerCase().includes("atendimento"));
			const queueId = atend ? atend.id : (queues[0] ? queues[0].id : null);
			let whatsappId = user?.whatsappId || null;
			if (!whatsappId) {
				try {
					const { data: wl } = await api.get("/whatsapp", { params: { companyId: user?.companyId, session: 0 } });
					const conn = (wl || []).find(w => w.status === "CONNECTED") || (wl || [])[0];
					if (conn) whatsappId = conn.id;
				} catch (e) {}
			}
			const { data: ticket } = await api.post("/tickets", {
				contactId: contact.id,
				queueId,
				whatsappId,
				userId: user?.id,
				status: "open",
				openExisting: true,
			});
			if (ticket?.userId && user?.id && ticket.userId !== user.id) {
				const nome = (ticket.user && ticket.user.name) || "outro atendente";
				toast.info(`Este paciente já está em atendimento com ${nome}.`);
			}
			if (ticket?.uuid) history.push(`/tickets/${ticket.uuid}`);
		} catch (err) {
			try {
				const t = JSON.parse(err?.response?.data?.error);
				if (t?.uuid) { history.push(`/tickets/${t.uuid}`); return; }
			} catch (e) {}
			toastError(err);
		}
	}, [user, history]);

	const CustomLink = React.useCallback(({ href, children, ...props }) => {
		if (href && href.indexOf("numlink:") === 0) {
			const number = href.slice("numlink:".length);
			return (
				<a
					href="#"
					onClick={e => { e.preventDefault(); e.stopPropagation(); openNumber(number); }}
					style={{ cursor: "pointer" }}
				>
					{children}
				</a>
			);
		}
		return (
			<a href={href} {...props} target="_blank" rel="noopener noreferrer">
				{children}
			</a>
		);
	}, [openNumber]);
	const boldRegex = /\*(.*?)\*/g;
	const tildaRegex = /~(.*?)~/g;

	const pixMarker = '###PIX_MARKER###';

	if (children && children.includes('***')) {
		children = children.replace(/\*\*\*/g, pixMarker);
	}

	if (children && boldRegex.test(children)) {
		children = children.replace(boldRegex, "**$1**");
	}

	if (children && children.includes(pixMarker)) {
		children = children.replace(new RegExp(pixMarker, 'g'), '***');
	}

	if (children && tildaRegex.test(children)) {
		children = children.replace(tildaRegex, "~~$1~~");
	}
	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: { component: CustomLink },
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, [CustomLink]);

	if (children) {
		children = children.replace(
			/(^|[\s(])(\+?\d[\d\s().-]{8,16}\d)(?=[\s).,;!?]|$)/g,
			(match, pre, raw) => {
				const digits = raw.replace(/\D/g, "");
				if (digits.length < 10 || digits.length > 13) return match;
				return `${pre}[${raw}](numlink:${digits})`;
			}
		);
	}

	if (!children) return null;

	return <Markdown options={options}>{children}</Markdown>;
};

export default MarkdownWrapper;
